import React, { useState } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase/firebaseConfig';
import { useNavigate, Link } from 'react-router-dom';
import { PageHeading, Section, Heading, Text } from '../components/ui';
import { useAuth } from '../context/authContext';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        
        try {
            await signInWithEmailAndPassword(auth, email, password);
            // Redirect to Explore page for walkthrough
            navigate('/explore');
        } catch (err) {
            // Provide user-friendly error messages
            if (err.message?.includes('user-not-found')) {
                setError('No account found with this email. Please register first.');
            } else if (err.message?.includes('wrong-password')) {
                setError('Incorrect password. Please try again.');
            } else if (err.message?.includes('invalid-email')) {
                setError('Please enter a valid email address.');
            } else if (err.message?.includes('too-many-requests')) {
                setError('Too many failed login attempts. Please try again later.');
            } else {
                setError(err.message || 'Login failed. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    // Eye icon component for password visibility
    const EyeIcon = ({ size = 20, className = '' }) => (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="currentColor"
            className={className}
            aria-hidden="true"
            style={{ border: 'none', outline: 'none' }}
        >
            <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
        </svg>
    );

    const EyeOffIcon = ({ size = 20, className = '' }) => (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="currentColor"
            className={className}
            aria-hidden="true"
            style={{ border: 'none', outline: 'none' }}
        >
            <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
        </svg>
    );

    return (
        <div className="min-h-screen bg-neutral-50 px-0">
            <div className="w-full py-8">
                {/* Header */}
                <PageHeading 
                    size="hero"
                    subtitle="ACCESS YOUR ACCOUNT"
                >
                    LOGIN
                </PageHeading>

                {/* Main Form Container */}
                <div className="bg-neutral-0 hairline minimal-padding vspace-xl mb-6 md:mb-8 mt-6 md:mt-8">
                    <form onSubmit={handleLogin} className="vspace">
                        {/* Email Field */}
                        <div>
                            <label htmlFor="email" className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">
                                EMAIL
                            </label>
                            <input
                                id="email"
                                type="email"
                                placeholder="your@email.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoComplete="email"
                                className="w-full p-3 border border-neutral-900 bg-neutral-0 font-mono text-sm text-neutral-900 placeholder-neutral-600 focus:outline-none focus:border-neutral-900"
                                disabled={loading}
                            />
                        </div>

                        {/* Password Field */}
                        <div>
                            <label htmlFor="password" className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">
                                PASSWORD
                            </label>
                            <div className="relative">
                                <input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Enter your password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    autoComplete="current-password"
                                    className="w-full p-3 pr-12 border border-neutral-900 bg-neutral-0 font-mono text-sm text-neutral-900 placeholder-neutral-600 focus:outline-none focus:border-neutral-900"
                                    disabled={loading}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-900 opacity-60 hover:text-neutral-400 transition-colors duration-120 focus:outline-none border-none bg-transparent p-0 m-0"
                                    style={{ border: 'none', outline: 'none', background: 'transparent', padding: 0, margin: 0 }}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    disabled={loading}
                                >
                                    {showPassword ? (
                                        <EyeOffIcon size={20} />
                                    ) : (
                                        <EyeIcon size={20} />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="bg-red-50 p-4 hairline border-red-200">
                                <Text size="sm" className="text-red-900 font-mono">
                                    {error}
                                </Text>
                            </div>
                        )}

                        {/* Submit Button */}
                        <button 
                            type="submit" 
                            disabled={loading}
                            className={`w-full py-3 px-6 hairline font-mono text-sm uppercase tracking-wider transition-opacity duration-120 ${
                                loading 
                                    ? 'bg-neutral-400 text-neutral-600 cursor-not-allowed' 
                                    : 'bg-neutral-900 text-neutral-0 hover:opacity-60'
                            }`}
                        >
                            {loading ? 'LOGGING IN...' : 'LOGIN'}
                        </button>
                    </form>

                    {/* Registration Link */}
                    <div className="mt-8 pt-6 hairline-t text-center">
                        <Text size="base" className="text-neutral-900 opacity-60 mb-2 font-mono">
                            Don't have an account?
                        </Text>
                        <Link 
                            to="/register" 
                            className="font-mono text-base uppercase tracking-wider text-neutral-900 opacity-60 hover:opacity-100 transition-opacity duration-120"
                        >
                            Register here →
                        </Link>
                    </div>
                </div>

                {/* Back to Home Link */}
                <div className="text-center">
                    <Link 
                        to="/" 
                        className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60 hover:opacity-100 transition-opacity duration-120"
                    >
                        ← Back to Home
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default Login;